import * as React from "react";

import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
} from "react-router-dom";

import { Helmet } from "react-helmet";

import {
  BrowserUpgradeNotificationNsw,
  ErrorBoundary,
  ScrollSink,
} from "@skv/core";

import { Banner, Breadcrumb, NavBar, SignInCta, SignInOutPanel } from "./ui";

import {
  BrowserUpgrade,
  Favourites,
  Home,
  MyActivity,
  SignIn,
  Unsubscribe,
  Volunteering,
  VolunteeringOrganisations,
} from "./pages";

import { routes as volunteeringOrganisationsRoutes } from "./pages/volunteering-organisations";

import { Error404, Error500, Footer, SearchFormWithCommitment } from "~/ui";
import { SearchResults } from "./ui/OpportunitySearchResults";
import { OrganisationSearchForm } from "./ui/OrganisationSearchForm";

import {
  favouritesStore,
  OpportunitySearchQueryState,
  OrganisationSearchQueryState,
  useAppSettings,
  usePageEnvelope,
  useThunkDispatch,
} from "@skv/state";

import OgImg from "./assets/og-image.png";
import { ROUTE_FAVOURITES, ROUTE_MY_ACTIVITY } from "./constants";
import { useUserState } from "./state";

export const App = (): JSX.Element => {
  const { isAuthenticated } = useUserState();
  const history = useHistory();
  const location = useLocation();
  const { applicationName, applicationUrl } = useAppSettings();
  const { pageEnvelope } = usePageEnvelope<{
    searchCriteria: OpportunitySearchQueryState | OrganisationSearchQueryState;
  }>();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  const dispatch = useThunkDispatch();

  // fetch initial favourites
  React.useEffect(() => {
    if (typeof window !== "undefined") {
      dispatch(favouritesStore.actions.getFavourites());

      if (pageEnvelope.data?.searchCriteria) {
        history.replace(`${location.pathname}${location.search}`, {
          searchQuery: pageEnvelope.data.searchCriteria,
        });
      }
    }
  }, []);

  return (
    <>
      <ScrollSink />
      <Helmet
        encodeSpecialCharacters={false}
        titleTemplate={`%s | ${applicationName}`}
        defaultTitle={`Volunteer in New South Wales | ${applicationName}`}
      >
        {/* Static Meta */}
        <meta name="keywords" content={`${applicationName}`} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={applicationName} />
        <meta property="og:image" content={`${applicationUrl}${OgImg}`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="1200" />
        {/* End Static */}

        <meta
          name="description"
          content={`Make a difference through volunteering. Volunteering enriches the lives of volunteers, strengthens local communities and benefits all. Find opportunities to volunteer with ${applicationName}`}
        />

        <meta property="og:url" content={applicationUrl} />

        <meta
          property="og:title"
          content={`Volunteer in New South Wales | ${applicationName}`}
        />
        <meta
          property="og:description"
          content={`Make a difference through volunteering. Volunteering enriches the lives of volunteers, strengthens local communities and benefits all. Find opportunities to volunteer with ${applicationName}`}
        />
      </Helmet>

      <nav
        aria-label="Skip to links"
        className="nsw-skip"
        css={`
          z-index: 9999;
          a:hover {
            color: var(--nsw-palette-white);
          }
        `}
      >
        <a href="#main-nav" aria-label="Skip to navigation">
          <div className="nsw-container">
            <span className="d-inline-block my-2 px-2">Skip to navigation</span>
          </div>
        </a>
        <a href="#content" aria-label="Skip to main content">
          <div className="nsw-container">
            <span className="d-inline-block my-2 px-2">
              Skip to main content
            </span>
          </div>
        </a>
      </nav>

      <NavBar
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
      />

      <div
        className="app-content-wrapper"
        inert={isMobileMenuOpen ? "true" : undefined}
        aria-hidden={isMobileMenuOpen ? "true" : undefined}
      >
        <Breadcrumb />
        <Banner />

        <Switch>
          <Route
            path={["/volunteering/unsubscribe", "/volunteering/keyunsubscribe"]}
            component={undefined}
          />
          <Route path="/volunteering/:id([0-9]*)" component={undefined} />
          <Route path="/volunteering-organisations" exact>
            <OrganisationSearchForm />
          </Route>
          <Route
            path={[
              "/*-volunteering-organisations",
              "/volunteering-organisations/in-*",
              "/find-organisations",
            ]}
          >
            <OrganisationSearchForm />
          </Route>
          <Route path="/volunteering-organisations/:id" component={undefined} />
          <Route path={["/browser-upgrade"]} component={undefined} />
          <Route
            path={["/favourites", "/signin", "/my-activity/*"]}
            component={undefined}
          />
          <Route>
            <SearchFormWithCommitment />
          </Route>
        </Switch>

        <main id="content">
          <div className="nsw-container">
            <SignInCta />
            <SignInOutPanel />

            <ErrorBoundary>
              {/* TODO : Figure out these routes */}
              <Switch>
                <Route exact path="/" component={Home} />
                <Route path="/signin">
                  <SignIn />
                </Route>
                <Route path="/browser-upgrade">
                  <BrowserUpgrade />
                </Route>
                <Route path="/my-activity">
                  <MyActivity />
                </Route>
                <Route path="/favourites">
                  {isAuthenticated ? (
                    <Redirect to={ROUTE_MY_ACTIVITY + ROUTE_FAVOURITES} />
                  ) : (
                    <Favourites />
                  )}
                </Route>
                <Route path={volunteeringOrganisationsRoutes}>
                  <VolunteeringOrganisations />
                </Route>

                <Route
                  path={[
                    "/volunteering/unsubscribe/:key",
                    "/volunteering/unsubscribe",
                  ]}
                >
                  <Unsubscribe />
                </Route>

                <Route path="/volunteering/:id([0-9]*)">
                  <Volunteering />
                </Route>

                {/* TODO : Look at better routing wildcards */}
                <Route
                  path={[
                    "/*-volunteering",
                    "/volunteering*",
                    "/find-opportunities",
                  ]}
                >
                  <SearchResults />
                </Route>

                <Route path="/error/500">
                  <Error500 />
                </Route>
                <Route path="/error/404">
                  <Error404 />
                </Route>
                <Route>
                  <Error404 />
                </Route>
              </Switch>
            </ErrorBoundary>
          </div>
        </main>

        <Footer />

        <BrowserUpgradeNotificationNsw />
      </div>
    </>
  );
};
